import { useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Col,
  Divider,
  Flex,
  Row,
  Select,
  Typography,
} from "antd";
import ListInput from "./components/Input";
import ListSelect from "./components/Select";
import ListDatePicker from "./components/DatePicker";
import NumericInput from "./components/NumericInput";
import ListMonthPicker from "./components/MonthPicker";

import { name_regexp, special_characters_regexp } from "helpers/regexp";

const getRegexp = (name) => {
  switch (name) {
    case "name":
    case "first_name":
    case "last_name":
      return name_regexp;
    case "ip_address":
      return new RegExp(/^[0-9.]{0,20}$/);
    case "post_code":
      return new RegExp(/^[0-9-]{0,6}$/);
    default:
      return special_characters_regexp;
  }
};

const FilterComponent = ({
  search_values,
  title,
  dataIndex: name,
  search_component = "input",
  search_type = "text",
  search_method,
  options = [],
  selectTagRender,
  setSearchValues,
  onSearch,
}) => {
  const value = useMemo(() => {
    if (options.length > 0) {
      if (search_component === "multi_select") {
        const available_values = options.map(({ value }) => value);
        return search_values?.[name]?.filter((item) =>
          available_values.includes(item)
        );
      }
      if (search_component === "select") {
        const available_values = options.map(({ value }) => value);
        if (available_values.includes(search_values?.[name])) {
          return search_values?.[name];
        }
        return null;
      }
    }
    return search_values?.[name];
    // eslint-disable-next-line
  }, [search_values?.[name], search_component]);

  return (
    <>
      {search_component === "input" &&
        (search_type === "number" ? (
          <NumericInput
            search_type={search_type}
            title={title}
            value={search_values?.[name] || ""}
            onChange={(value) =>
              setSearchValues({
                ...search_values,
                [name]: value,
              })
            }
            onBlur={() =>
              onSearch({
                name,
                search_method,
                value: search_values?.[name],
              })
            }
            onPressEnter={() =>
              onSearch({
                name,
                search_method,
                value: search_values?.[name],
              })
            }
          />
        ) : (
          <ListInput
            search_type={search_type}
            regexp={getRegexp(name)}
            title={title}
            value={search_values?.[name] || ""}
            onChange={(value) =>
              setSearchValues({
                ...search_values,
                [name]: value,
              })
            }
            onBlur={() =>
              onSearch({
                name,
                search_method,
                value: search_values?.[name],
              })
            }
            onPressEnter={() =>
              onSearch({
                name,
                search_method,
                value: search_values?.[name],
              })
            }
          />
        ))}
      {search_component === "select" && (
        <ListSelect
          title={title}
          options={options}
          value={value}
          onChange={(value) => [
            setSearchValues({
              ...search_values,
              [name]: value,
            }),
            onSearch({
              name,
              search_method,
              value,
            }),
          ]}
        />
      )}
      {search_component === "multi_select" && (
        <ListSelect
          title={title}
          options={options}
          is_multi
          value={value}
          selectTagRender={selectTagRender}
          onChange={(value) => [
            setSearchValues({
              ...search_values,
              [name]: value,
            }),
            onSearch({
              name,
              search_method,
              value,
            }),
          ]}
        />
      )}
      {search_component === "date-picker" && (
        <ListDatePicker
          title={title}
          value={search_values?.[name]}
          onChange={(value) => [
            setSearchValues({
              ...search_values,
              [name]: value,
            }),
            onSearch({
              name,
              search_method,
              value,
            }),
          ]}
        />
      )}
      {search_component === "month-picker" && (
        <ListMonthPicker
          title={title}
          value={search_values?.[name]}
          onChange={(value) => [
            setSearchValues({
              ...search_values,
              [name]: value,
            }),
            onSearch({
              name,
              search_method,
              value,
            }),
          ]}
        />
      )}
    </>
  );
};

const FilterComponents = ({
  columns,
  search_values,
  setSearchValues,
  onSearch,
  col_xl = 4,
  col_lg = 6,
  col_md = 12,
  col_xs = 24,
}) =>
  columns
    .filter(({ searchable }) => searchable)
    .map((item) =>
      item.is_important ? (
        <div style={{ minWidth: 220 }}>
          <FilterComponent
            search_values={search_values}
            onSearch={onSearch}
            setSearchValues={setSearchValues}
            {...item}
          />
        </div>
      ) : (
        <Col xs={col_xs} md={col_md} lg={col_lg} xl={col_xl}>
          <FilterComponent
            search_values={search_values}
            onSearch={onSearch}
            setSearchValues={setSearchValues}
            {...item}
          />
        </Col>
      )
    );

const Filters = ({
  query = {},
  columns,
  search_values,
  setSearchValues,
  onSearch,
  setQuery,
  paginate,
  setPaginate,
  total_count,
  show_checklist,
  checked_list,
  updateCheckedList,
  col_xl,
  col_lg,
  col_md,
  col_xs,
}) => {
  const checked_list_options = columns.map(({ dataIndex, title }) => ({
    label: title,
    value: dataIndex,
  }));

  const [show_columns, setShowColumns] = useState(false);

  const sort_options = useMemo(() => {
    const data = [];
    columns
      .filter(({ sortable }) => sortable)
      .forEach(({ dataIndex, title }) => {
        data.push({
          label: `${title} malejąco`,
          value: `${dataIndex}__desc`,
        });
        data.push({
          label: `${title} rosnąco`,
          value: `${dataIndex}__asc`,
        });
      });
    return data;
  }, [columns]);

  const important_columns = useMemo(
    () => columns.filter(({ is_important = false }) => is_important),
    [columns]
  );

  const standard_columns = useMemo(
    () => columns.filter(({ is_important = false }) => !is_important),
    [columns]
  );

  return (
    <>
      <Row gutter={[12, 12]}>
        <FilterComponents
          columns={standard_columns}
          search_values={search_values}
          onSearch={onSearch}
          setSearchValues={setSearchValues}
          {...{ col_xl, col_lg, col_md, col_xs }}
        />
      </Row>
      <Flex gap={12} wrap className="m-t-20 m-b-20">
        <Flex align="center" wrap gap={12} style={{ flex: 1 }}>
          <Select
            allowClear
            style={{
              minWidth: 240,
            }}
            placeholder="Sortuj"
            value={sort_options.find(({ value }) => value === paginate?.sort)}
            onChange={(value) => {
              setPaginate({
                ...paginate,
                page: undefined,
                sort: value,
              });
            }}
            options={sort_options}
          />
          <FilterComponents
            columns={important_columns}
            search_values={search_values}
            onSearch={onSearch}
            setSearchValues={setSearchValues}
          />
        </Flex>
        <Flex gap={12} wrap align="center">
          <Typography.Text>Ilość: {total_count}</Typography.Text>
          <Button type="primary">Szukaj</Button>
          <Button
            onClick={() => {
              const important_keys = new Set(
                important_columns.map(({ dataIndex }) => dataIndex)
              );

              const important_search_values = Object.fromEntries(
                Object.entries(search_values).filter(([key]) =>
                  important_keys.has(key)
                )
              );

              const important_query = Object.fromEntries(
                Object.entries(query).filter(([key]) => important_keys.has(key))
              );

              setSearchValues(important_search_values);

              setPaginate({
                ...paginate,
                page: undefined,
                per_page: undefined,
                sort: undefined,
              });
              setQuery(important_query);
            }}
          >
            Resetuj
          </Button>
          {show_checklist && (
            <Button onClick={() => setShowColumns(!show_columns)}>
              {!show_columns ? "Pokaż" : "Ukryj"} kolumny
            </Button>
          )}
        </Flex>
      </Flex>
      {show_checklist && show_columns && (
        <>
          <Divider />
          <Checkbox.Group
            value={checked_list}
            options={checked_list_options}
            onChange={updateCheckedList}
          />
          <Divider />
        </>
      )}
    </>
  );
};

export default Filters;
